export const openInNewTab = (href: string) => {
  Object.assign(document.createElement('a'), {
    target: '_blank',
    href
  }).click();
};

// Compute relative luminance (WCAG 2.0) without polished dependency
const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_RE = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/;

export function parseHexColor(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16)
  ];
}

function parseColor(color: string): [number, number, number] | null {
  if (HEX_RE.test(color)) return parseHexColor(color);
  const match = color.match(RGB_RE);
  if (match) {
    // Reject semi-transparent rgba — luminance depends on the unknown base background
    if (match[4] !== undefined && parseFloat(match[4]) < 1) return null;
    return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
  }
  return null;
}

function srgbToLinear(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function getLuminance(color: string): number {
  const rgb = parseColor(color);
  if (!rgb) return NaN;
  return (
    0.2126 * srgbToLinear(rgb[0]) + 0.7152 * srgbToLinear(rgb[1]) + 0.0722 * srgbToLinear(rgb[2])
  );
}

// NaN luminance (unparseable input) assumes light background for safe dark text
export const isDark = (bgColor: string) => getLuminance(bgColor) < 0.5;

export const getTextColor = (bgColor: string) => (isDark(bgColor) ? '#F3F4F6' : '#374151');

/** Parse a CSS px value — accepts "10px", "10", or 10. Returns number or defaultValue. */
export const parsePx = (value?: string | number, defaultValue?: number): number | undefined => {
  if (value == null) return defaultValue;
  if (typeof value === 'number') return value;
  const parsed = parseInt(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/** Convert a value to CSS px string — 10 → "10px", "10px" → "10px", undefined → defaultValue */
export const toCssPx = (value?: string | number, defaultValue?: string): string | undefined => {
  if (value == null) return defaultValue;
  return typeof value === 'number' ? `${value}px` : value || defaultValue;
};

export const appendHiddenField = (form: HTMLFormElement, name: string, value?: string) => {
  if (!value) return;
  const field = document.createElement('input');
  field.type = 'hidden';
  field.name = name;
  field.value = value;
  form.appendChild(field);
};

export const antiKeylogger = () => {
  // @ts-ignore
  window.onkeydown = undefined;
  // @ts-ignore
  window.onkeypress = undefined;
  // @ts-ignore
  window.onkeyup = undefined;
  // @ts-ignore
  document.onkeydown = undefined;
  // @ts-ignore
  document.onkeypress = undefined;
  // @ts-ignore
  document.onkeyup = undefined;
};

export const formatShopName = (shopName = '') => {
  return shopName.split(' | ')[0];
};

const lngMap: Record<string, string> = {
  ca: 'es_ES',
  en: 'en_GB',
  lv: 'en_GB',
  et: 'en_GB'
};

export const getLocale = (language?: string) => {
  if (!language) return undefined;
  if (language.length > 2) return language.replace('-', '_');
  if (lngMap[language]) return lngMap[language];
  return `${language}_${language.toUpperCase()}`;
};

export const openPopup = (params: {
  url?: string;
  title?: string;
  width: number;
  height: number;
}): Window => {
  // Fixes dual-screen position                             Most browsers      Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height;

  const left = (width - params.width) / 2 + dualScreenLeft;
  const top = (height - params.height) / 2 + dualScreenTop;
  const newWindow = window.open(
    params.url || '',
    params.title,
    `
      scrollbars=yes,
      width=${params.width}, 
      height=${params.height}, 
      top=${top}, 
      left=${left}
      `
  );

  if (!newWindow) {
    throw new Error('POPUP_BLOCKED');
  }
  if (newWindow.focus) newWindow.focus();
  return newWindow;
};

export const isSafari = () => /apple/i.test(navigator.vendor);

export const obfuscatePhone = (phoneNumber?: string) => {
  if (!phoneNumber) return undefined;
  return phoneNumber.replace(/(\d{2})(\d{6})(\d{3})/, '$1******$3');
};
